export type ApiErrorBody = {
  error?: string;
  message?: string;
  action?: string;
  details?: Record<string, unknown>;
  success?: boolean;
  detail?: unknown;
};

export type ParsedApiError = {
  slug: string;
  message: string;
  action?: string;
  details?: Record<string, unknown>;
  retryAfterSeconds?: number;
  rawBody?: unknown;
};

function extractRetryAfterSeconds(details?: Record<string, unknown>): number | undefined {
  const retryRaw = details?.retry_after_seconds;
  return typeof retryRaw === "number" && Number.isFinite(retryRaw) ? retryRaw : undefined;
}

/** Parse an already-read JSON error body (Phase 2 envelope). */
export function parseApiErrorBody(body: ApiErrorBody, statusText = ""): ParsedApiError {
  if ("success" in body && body.success === false) {
    return {
      slug: typeof body.error === "string" ? body.error : "broker.connection_failed",
      message: typeof body.message === "string" ? body.message : "Connection failed.",
      action: typeof body.action === "string" ? body.action : undefined,
      rawBody: body,
    };
  }

  if (typeof body.error === "string" && typeof body.message === "string") {
    const details =
      body.details && typeof body.details === "object" && !Array.isArray(body.details)
        ? body.details
        : undefined;
    return {
      slug: body.error,
      message: body.message,
      action: typeof body.action === "string" ? body.action : undefined,
      details,
      retryAfterSeconds: extractRetryAfterSeconds(details),
      rawBody: body,
    };
  }

  if (typeof body.message === "string" && body.message.trim()) {
    const details =
      body.details && typeof body.details === "object" && !Array.isArray(body.details)
        ? body.details
        : undefined;
    return {
      slug: typeof body.error === "string" ? body.error : "http_error",
      message: body.message,
      action: typeof body.action === "string" ? body.action : undefined,
      details,
      retryAfterSeconds: extractRetryAfterSeconds(details),
      rawBody: body,
    };
  }

  if (typeof body.detail === "string" && body.detail.trim()) {
    return {
      slug: "http_error",
      message: body.detail.trim(),
      rawBody: body,
    };
  }

  return {
    slug: "http_error",
    message: statusText || "Something went wrong.",
    rawBody: body,
  };
}

/** Parse a failed HTTP response body per FE_ERROR_CONTRACT.md. */
export async function parseApiError(response: Response): Promise<ParsedApiError> {
  let body: ApiErrorBody = {};
  try {
    body = (await response.json()) as ApiErrorBody;
  } catch {
    return {
      slug: "http_error",
      message: response.statusText || "Something went wrong.",
    };
  }

  return parseApiErrorBody(body, response.statusText || "Something went wrong.");
}
